/**
 * TanStack Query hooks for `affiliate-manager-assignment`.
 *
 * The "list" query drives tab 1 (Assignments); the "workload" query drives
 * tab 2 (Manager Workload). Both are invalidated after assign / unassign so
 * the two tabs stay in sync.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	affiliateManagerAssignmentQueryKeys,
	assign,
	listAssignments,
	listManagers,
	unassign,
	workloadStats,
	type AssignArgs,
	type AssignResult,
	type AssignmentView,
	type ManagerWorkload,
} from "./api";

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useAffiliateManagerAssignmentList(): UseQueryResult<
	AssignmentView[],
	Error
> {
	return useQuery({
		queryKey: affiliateManagerAssignmentQueryKeys.list(),
		queryFn: () => listAssignments(),
	});
}

export function useAffiliateManagerWorkload(): UseQueryResult<
	ManagerWorkload[],
	Error
> {
	return useQuery({
		queryKey: affiliateManagerAssignmentQueryKeys.workload(),
		queryFn: () => workloadStats(),
	});
}

export function useAffiliateManagerRoster(): UseQueryResult<
	readonly string[],
	Error
> {
	return useQuery({
		queryKey: affiliateManagerAssignmentQueryKeys.managers(),
		queryFn: () => listManagers(),
	});
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useAffiliateManagerAssignmentCreate(): UseMutationResult<
	AssignResult,
	Error,
	AssignArgs
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: assign,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: affiliateManagerAssignmentQueryKeys.list(),
			});
			qc.invalidateQueries({
				queryKey: affiliateManagerAssignmentQueryKeys.workload(),
			});
		},
	});
}

export function useAffiliateManagerAssignmentDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: unassign,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: affiliateManagerAssignmentQueryKeys.list(),
			});
			qc.invalidateQueries({
				queryKey: affiliateManagerAssignmentQueryKeys.workload(),
			});
		},
	});
}
